import React from 'react';
import { Link } from 'react-router-dom';
import { 
  EnvelopeIcon, 
  MapPinIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const companyLinks = [
    { name: 'About Us', href: '/about' },
    { name: 'Contact', href: '/contact' },
    { name: 'Services', href: '/services' },
    { name: 'News', href: '/news' },
  ];

  const tradingLinks = [
    { name: 'Trading Platform', href: '/trading' },
    { name: 'Markets', href: '/markets' },
    { name: 'Portfolio', href: '/portfolio' },
    { name: 'Research', href: '/news' },
    { name: 'Copy Trading', href: '/copy-trading' },
  ];

  const resourceLinks = [
    { name: 'Account', href: '/account' },
    { name: 'Login', href: '/login' },
    { name: 'Register', href: '/register' },
    { name: 'Contact Support', href: '/contact' },
  ];

  const legalLinks = [
    { name: 'Privacy Policy', href: '/privacy-policy' },
    { name: 'Terms of Service', href: '/terms-of-service' },
    { name: 'Risk Disclosure', href: '/risk-disclosure' },
    { name: 'Regulatory Information', href: '/regulatory-information' },
  ];

  const features = [
    {
      icon: ShieldCheckIcon,
      title: 'Bank-Level Security',
      description: 'Your funds and data are protected with industry-leading security measures.',
    },
    {
      icon: CurrencyDollarIcon,
      title: 'Low Fees',
      description: 'Competitive pricing with transparent fee structure and no hidden costs.',
    },
    {
      icon: ChartBarIcon,
      title: 'Advanced Tools',
      description: 'Professional trading tools and real-time market analysis.',
    },
  ];

  return (
    <footer className="bg-gray-900 border-t border-gray-800">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Features Section */}
        <div className="py-12 border-b border-gray-800">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center">
                  <feature.icon className="h-8 w-8 text-blue-400" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-white">{feature.title}</h3>
                <p className="mt-2 text-sm text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://res.cloudinary.com/dq0hqpe7f/image/upload/v1750281706/crestedgelogo-white_uc0jbg.png" 
                  alt="Crest Edge Brokerage Logo" 
                  className="h-12 w-auto"
                />
              </div>
              <p className="text-gray-300 mb-6 max-w-md">
                Experience the future of trading with our advanced platform. Trade stocks, 
                cryptocurrencies, forex, and commodities with confidence.
              </p>
              
              {/* Contact Info */}
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <EnvelopeIcon className="h-5 w-5 text-gray-500" />
                  <span className="text-sm text-gray-300">support@crestedgebrokerage.com</span>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPinIcon className="h-5 w-5 text-gray-500" />
                  <span className="text-sm text-gray-300">Bolton, England, BL3 5RR</span>
                </div>
              </div>
            </div>

            {/* Links Sections */}
            <div>
              <h3 className="text-sm font-semibold text-gray-200 tracking-wider uppercase mb-4">
                Company
              </h3>
              <ul className="space-y-3">
                {companyLinks.map((link) => (
                  <li key={link.name}>
                    <Link 
                      to={link.href} 
                      className="text-sm text-gray-400 hover:text-blue-400 transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-200 tracking-wider uppercase mb-4">
                Trading
              </h3>
              <ul className="space-y-3">
                {tradingLinks.map((link) => (
                  <li key={link.name}>
                    <Link 
                      to={link.href} 
                      className="text-sm text-gray-400 hover:text-blue-400 transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-200 tracking-wider uppercase mb-4">
                Resources
              </h3>
              <ul className="space-y-3">
                {resourceLinks.map((link) => (
                  <li key={link.name}>
                    <Link 
                      to={link.href} 
                      className="text-sm text-gray-400 hover:text-blue-400 transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-200 tracking-wider uppercase mb-4">
                Legal
              </h3>
              <ul className="space-y-3">
                {legalLinks.map((link) => (
                  <li key={link.name}>
                    <Link 
                      to={link.href} 
                      className="text-sm text-gray-400 hover:text-blue-400 transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="py-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-wrap justify-center md:justify-start space-x-6">
              {legalLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-xs text-gray-500 hover:text-blue-400 transition-colors"
                >
                  {link.name}
                </Link>
              ))}
            </div>
            
            <div className="text-center md:text-right">
              <p className="text-xs text-gray-500">
                © {currentYear} Crest Edge Brokerage. All rights reserved.
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Securities offered through licensed broker-dealers. Member FINRA/SIPC.
              </p>
            </div>
          </div>
        </div>

        {/* Additional Dark Theme Elements */}
        <div className="pb-8">
          <div className="flex justify-center space-x-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-400 mb-1">24/7</div>
              <div className="text-xs text-gray-500">Support</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-400 mb-1">99.9%</div>
              <div className="text-xs text-gray-500">Uptime</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-400 mb-1">500K+</div>
              <div className="text-xs text-gray-500">Users</div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 